/*
| Presentation helpers, ported from the prototype.
|
| ONE RULE GOVERNS THIS FILE: null is not zero.
|
| The API returns null for anything it cannot measure — TVL before the indexer
| reaches a period, open interest while /positions is unbuilt, PnL with no
| mark-price feed — and tags each with a readiness. If a formatter coerces that
| null into "$0.00" the entire contract the backend enforces is thrown away at
| the last step, and the number looks like a measurement. So every formatter here
| takes null and returns an em dash.
*/

export const EMPTY = '—';

const isBlank = (n) => n === null || n === undefined || n === '';

/** Decimal strings arrive as strings on purpose (numeric columns); parse late. */
const num = (n) => (typeof n === 'number' ? n : Number.parseFloat(n));

export function usd(value, { empty = EMPTY } = {}) {
  if (isBlank(value)) return empty;
  const n = num(value);
  if (!Number.isFinite(n)) return empty;

  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(2)}`;
}

export function fnum(value, decimals) {
  if (isBlank(value)) return EMPTY;
  const n = num(value);
  if (!Number.isFinite(n)) return EMPTY;

  if (n >= 1e9) return `${(n / 1e9).toFixed(decimals ?? 2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(decimals ?? 2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(decimals ?? 1)}K`;
  return n.toFixed(decimals ?? (n < 1 ? 4 : 2));
}

export function comma(value) {
  if (isBlank(value)) return EMPTY;
  const n = num(value);
  return Number.isFinite(n) ? n.toLocaleString('en-US') : EMPTY;
}

export function percent(value, { signed = true } = {}) {
  if (isBlank(value)) return EMPTY;
  const n = num(value);
  if (!Number.isFinite(n)) return EMPTY;
  return `${signed && n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

/** Short 0x form: 0x71C7…976F */
export function short(value, head = 6, tail = 4) {
  if (isBlank(value)) return EMPTY;
  return value.length > head + tail + 2
    ? `${value.slice(0, head + 2)}…${value.slice(-tail)}`
    : value;
}

export function ago(timestamp) {
  if (isBlank(timestamp)) return EMPTY;
  const then = new Date(timestamp).getTime();
  if (Number.isNaN(then)) return EMPTY;

  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * How long UNTIL a future timestamp — the mirror of ago().
 *
 * ago() clamps negatives to zero, so a future time reads "0s ago" through it:
 * not a lie about the past, but a wrong statement about the future, and the
 * funding countdown is the one place the explorer points forwards.
 */
export function until(timestamp) {
  if (isBlank(timestamp)) return EMPTY;
  const then = new Date(timestamp).getTime();
  if (Number.isNaN(then)) return EMPTY;

  const seconds = Math.floor((then - Date.now()) / 1000);

  // Already elapsed: the schedule rolled over and upstream has not published the
  // next one yet. "due" states that; a negative countdown would not.
  if (seconds <= 0) return 'due';
  if (seconds < 60) return `in ${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${hours}h`;
  return `in ${Math.floor(hours / 24)}d`;
}

export function dateTime(timestamp) {
  if (isBlank(timestamp)) return EMPTY;
  const d = new Date(timestamp);
  return Number.isNaN(d.getTime()) ? EMPTY : d.toUTCString();
}

/** Escapes interpolated values — every screen builds HTML strings. */
export function esc(value) {
  if (isBlank(value)) return '';
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * A price at full precision: grouped, never compacted to K/M, and with enough
 * significant digits that a small price never reads as $0.0000.
 */
export function price(value) {
  if (isBlank(value)) return EMPTY;
  const n = num(value);
  if (!Number.isFinite(n)) return EMPTY;

  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  if (abs === 0) return '$0.00';
  if (abs >= 1) {
    return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: abs >= 1000 ? 2 : 4 })}`;
  }

  // Four significant digits below a dollar, trailing zeros trimmed to two places.
  const places = Math.min(20, 3 - Math.floor(Math.log10(abs)));
  const fixed = abs.toFixed(places).replace(/0+$/, '');
  const [whole, fraction = ''] = fixed.split('.');
  return `${sign}$${whole}.${fraction.padEnd(2, '0')}`;
}

export const cap = (s) => (isBlank(s) ? '' : s.charAt(0).toUpperCase() + s.slice(1));

/** A token amount from its exact decimal string: grouped, never rounded or abbreviated. */
export function amount(value) {
  if (isBlank(value)) return EMPTY;
  const text = String(value).trim();
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) return EMPTY;
  const [, sign, whole, fraction] = match;
  return `${sign}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction ? `.${fraction}` : ''}`;
}

/**
 * A signed dollar figure, coloured by direction. A value that rounds to $0.00
 * at the displayed precision is neutral and unsigned: never "-$0.00".
 */
export function signedUsd(value) {
  if (isBlank(value)) return `<span class="muted">${EMPTY}</span>`;
  const n = num(value);
  if (!Number.isFinite(n)) return `<span class="muted">${EMPTY}</span>`;
  const text = usd(Math.abs(n));
  if (text === usd(0)) return text;
  return n > 0 ? `<span class="up">+${text}</span>` : `<span class="down">-${text}</span>`;
}
